import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getDeviceFingerprint } from '@/utils/fingerprint'
import { generateSignatureHeaders } from '@/utils/captchaSignature'

// 根据环境确定基础URL
const getBaseURL = () => {
  // 如果有环境变量设置，优先使用
  if (process.env.VUE_APP_API_BASE_URL) {
    return process.env.VUE_APP_API_BASE_URL
  }
  // 开发环境
  if (process.env.NODE_ENV === 'development') {
    return '/api'
  }
  // 生产环境 - 添加h5-api前缀
  return '/api'
}

// 创建axios实例
const http = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

const markErrorAsNotified = error => {
  error.notified = true
  return error
}

// 业务 catch 仅处理未经过统一响应拦截器的异常，避免重复弹窗。
export const showErrorIfNotNotified = (error, fallbackMessage) => {
  if (!error?.notified) {
    ElMessage.error(fallbackMessage)
  }
}

// 请求拦截器
http.interceptors.request.use(
  async config => {
    // 检查是否跳过认证（用于不需要登录的接口）
    const skipAuth = config.skipAuth || false

    // 从localStorage获取token并添加到请求头（除非标记为跳过认证）
    if (!skipAuth) {
      const token = localStorage.getItem('tianqi-token')
      if (token) {
        config.headers['Authorization'] = token
      }
    }

    // 添加设备指纹签名头（用于风控验证）
    try {
      const deviceFingerprint = await getDeviceFingerprint()
      const signatureHeaders = generateSignatureHeaders(deviceFingerprint)

      // 合并签名头到请求头
      Object.assign(config.headers, signatureHeaders)
    } catch (error) {
      console.error('生成设备指纹签名失败:', error)
      // 签名生成失败不阻塞请求，让后端决定是否拦截
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  response => {
    const res = response.data

    // 状态码不是200，视为异常
    if (res.status !== 200) {
      ElMessage.error(res.msg || '请求失败')

      // 401: 未登录或token过期（但跳过认证的接口不处理）
      if (res.status === 401 && !response.config.skipAuth) {
        // 清除登录信息
        localStorage.removeItem('tianqi-token')
        localStorage.removeItem('tianqi-user')

        // 可以在这里添加跳转到登录页面的逻辑
        setTimeout(() => {
          window.location.href = '/login'
        }, 1500)
      }

      return Promise.reject(markErrorAsNotified(new Error(res.msg || '请求失败')))
    } else {
      return res
    }
  },
  error => {
    ElMessage.error(error.message || '网络异常，请稍后重试')
    return Promise.reject(markErrorAsNotified(error))
  }
)

export default http
